import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Who is signed in. The server-side session cookie is the real gate (the API answers 401 when
// it's missing); this context keeps a display copy of the user in localStorage so a reload
// doesn't flash the login screen, and signs out locally when the server says the session ended.
// Exposes { currentUser, isAuthorized, loading, authError, login(identifier, password), logout() }.

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

const STORE_USER = 'dugoutscout_user';
const STORE_SINCE = 'dugoutscout_login_time';
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // sign in again every 72 hours

// Report picks remembered per browser; wiped whenever the signed-in person changes.
const PERSONAL_KEYS = [
  'infieldSelectedTeam', 'infieldSelectedYear', 'infieldSelectedPlayers', 'infieldReportMode',
  'lineupPlayers', 'benchPlayers', 'heatmapData', 'reportDate', 'opponentName',
  'dugoutscout_recent_teams',
];

const session = {
  read() {
    let user = null;
    try { user = JSON.parse(localStorage.getItem(STORE_USER)); } catch { /* corrupt entry */ }
    if (!user) { localStorage.removeItem(STORE_SINCE); return null; }
    const since = Number(localStorage.getItem(STORE_SINCE));
    if (!since) { localStorage.setItem(STORE_SINCE, String(Date.now())); return user; } // older stores had no time
    if (Date.now() - since > MAX_AGE_MS) { session.clear(); return null; }
    return user;
  },
  expired() {
    const since = Number(localStorage.getItem(STORE_SINCE));
    return !since || Date.now() - since > MAX_AGE_MS;
  },
  save(user) {
    localStorage.setItem(STORE_USER, JSON.stringify(user));
    localStorage.setItem(STORE_SINCE, String(Date.now()));
  },
  clear() {
    [STORE_USER, STORE_SINCE, ...PERSONAL_KEYS].forEach((k) => localStorage.removeItem(k));
  },
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(session.read);
  const [authError, setAuthError] = useState(null);

  const logout = useCallback(() => {
    session.clear();
    setCurrentUser(null);
    setAuthError(null);
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {}); // local sign-out never waits on this
  }, []);

  // Any API call answered 401 means the server session is gone: sign out here too. Wrapping
  // fetch once covers every request in the app.
  useEffect(() => {
    if (window.__dsAuthWatch) return;
    window.__dsAuthWatch = true;
    const send = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const res = await send(input, init);
      const url = typeof input === 'string' ? input : input?.url || '';
      if (res.status === 401 && url.startsWith('/api/') && url !== '/api/auth/login') logout();
      return res;
    };
  }, [logout]);

  // Enforce the 72-hour limit while the app stays open.
  useEffect(() => {
    if (!currentUser) return undefined;
    const check = () => { if (session.expired()) logout(); };
    check();
    const timer = setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => { clearInterval(timer); window.removeEventListener('focus', check); };
  }, [currentUser, logout]);

  const login = useCallback(async (identifier, password) => {
    setAuthError(null);
    let reply;
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: identifier, password }), // email or username
      });
      reply = await res.json();
    } catch {
      setAuthError('Could not reach the server. Try again.');
      return false;
    }
    if (!reply.success) {
      setAuthError(reply.error || 'That email/username and password did not match.');
      return false;
    }
    session.clear();
    session.save(reply.user);
    setCurrentUser(reply.user);
    return true;
  }, []);

  const value = useMemo(() => ({
    currentUser, isAuthorized: !!currentUser, loading: false, authError, login, logout,
  }), [currentUser, authError, login, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
